import React, { useEffect, useMemo, useState } from 'react';
import {
    Cell,
    Legend,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
} from 'recharts';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ADMIN_QUESTIONS_API = process.env.REACT_APP_ADMIN_QUESTIONS_API || `${API_BASE_URL}/api/admin-app/aptitude-questions/`;

const STATUS_COLORS = {
    draft: '#94a3b8',
    submitted: '#2563eb',
    published: '#10b981',
    unknown: '#64748b',
};

const DIFFICULTY_COLORS = {
    Easy: '#10b981',
    Medium: '#f59e0b',
    Hard: '#ef4444',
    Unknown: '#64748b',
};

const getAuthHeaders = () => {
    const token = (localStorage.getItem('guest_access_token') || sessionStorage.getItem('guest_access_token') || '').trim();
    if (!token) {
        return null;
    }

    return {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
    };
};

const ChartCard = ({ title, subtitle, children }) => (
    <div className="admin-panel-card admin-chart-card">
        <div className="admin-chart-header">
            <div>
                <h3 className="admin-chart-title">{title}</h3>
                <p className="admin-chart-sub">{subtitle}</p>
            </div>
        </div>
        <div className="admin-chart-canvas">
            {children}
        </div>
    </div>
);

const AdminReportsPage = () => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);

    const authHeaders = useMemo(() => getAuthHeaders(), []);

    useEffect(() => {
        const load = async () => {
            if (!authHeaders) {
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(ADMIN_QUESTIONS_API, { headers: authHeaders });
                const data = response.ok ? await response.json() : [];
                setQuestions(Array.isArray(data) ? data : []);
            } catch (error) {
                setQuestions([]);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [authHeaders]);

    const questionStatusData = useMemo(() => {
        const counts = questions.reduce((accumulator, item) => {
            const status = ['draft', 'submitted', 'published'].includes(item.status) ? item.status : 'unknown';
            accumulator[status] = (accumulator[status] || 0) + 1;
            return accumulator;
        }, {});

        return ['draft', 'submitted', 'published', 'unknown']
            .filter((status) => (counts[status] || 0) > 0)
            .map((status) => ({
                name: status.charAt(0).toUpperCase() + status.slice(1),
                value: counts[status],
                color: STATUS_COLORS[status],
            }));
    }, [questions]);

    const questionDifficultyData = useMemo(() => {
        const counts = questions.reduce((accumulator, item) => {
            const difficulty = ['Easy', 'Medium', 'Hard'].includes(item.difficulty) ? item.difficulty : 'Unknown';
            accumulator[difficulty] = (accumulator[difficulty] || 0) + 1;
            return accumulator;
        }, {});

        return ['Easy', 'Medium', 'Hard', 'Unknown']
            .filter((difficulty) => (counts[difficulty] || 0) > 0)
            .map((difficulty) => ({
                name: difficulty,
                value: counts[difficulty],
                color: DIFFICULTY_COLORS[difficulty],
            }));
    }, [questions]);

    return (
        <div className="admin-dashboard-shell">
            <div className="admin-dashboard-hero admin-panel-card">
                <div>
                    <p className="admin-dashboard-eyebrow">Admin reports</p>
                    <h3 className="admin-dashboard-title">Question analytics</h3>
                    <p className="admin-dashboard-summary">
                        A compact view of question status and difficulty distribution across the full question bank.
                    </p>
                </div>
            </div>

            <div className="admin-dashboard-grid admin-dashboard-grid--split">
                <ChartCard
                    title="Question status mix"
                    subtitle="How questions are moving through draft, submitted, and published states."
                >
                    {loading || questionStatusData.length === 0 ? (
                        <div className="admin-chart-empty">No question status data yet.</div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={questionStatusData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={70}
                                    outerRadius={110}
                                    paddingAngle={4}
                                    stroke="none"
                                >
                                    {questionStatusData.map((entry) => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>

                <ChartCard
                    title="Question difficulty mix"
                    subtitle="Difficulty spread across the full question bank."
                >
                    {loading || questionDifficultyData.length === 0 ? (
                        <div className="admin-chart-empty">No difficulty data yet.</div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={questionDifficultyData}
                                    dataKey="value"
                                    nameKey="name"
                                    innerRadius={70}
                                    outerRadius={110}
                                    paddingAngle={4}
                                    stroke="none"
                                >
                                    {questionDifficultyData.map((entry) => (
                                        <Cell key={entry.name} fill={entry.color} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend />
                            </PieChart>
                        </ResponsiveContainer>
                    )}
                </ChartCard>
            </div>
        </div>
    );
};

export default AdminReportsPage;